import swal from "sweetalert"
import randomstring from "randomstring"

// action creators
import {
    resourceGetDetail,
    resourceGetList,
    resourcePatchDetail,
    resourcePostList,
} from "shared/djangio/action-creators"
import { segueToUrl } from "global-action-creators"
import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

// TODO: add this back
// import { sendUserActionPost } from "shared/user-actions/user-actions-action-creators"

// action types
import dashboardsActionTypes, * as actionDicts from "app/static/frontend/dashboards/action-types"

// selectors
import { selectDashboardsValue } from "app/static/frontend/dashboards/selectors"
// TODO: use this syntax instead if we need to dehydrate_extra
// const Dashboard = DjangIO.app.widgets.models.Dashboard.objects.filter({}),

import constants from "app/static/frontend/dashboards/constants"

// models
const { Dashboard } = DjangIO.app.widgets.models

/**
 * https://github.com/STRML/react-grid-layout/blob/master/test/examples/6-dynamic-add-remove.jsx
 *
 * @param {int} id - the id of the dashboard
 * @param {int} index - the index of a given item in the RGL layout
 * @param {Func} removeFromDashboardLayout - removes key/value from layout/newLayout in the Dashboard store
 */
export const onRemoveItemHandler = ({ id, index, removeFromDashboardLayout }) => {
    removeFromDashboardLayout({
        id,
        index,
    })
}

/**
 * Destroys the Dashboard in the store
 *
 * @param {int} id - the id of the dashboard
 */
export const destroyDashboardStore = ({ id } = {}) => ({
    type: dashboardsActionTypes.DESTROY_DASHBOARD_STORE,
    id,
})

/**
 * Initializes the Dashboard store
 * Fired when we first mount the Dashboard
 *
 * @param {bool} editing - whether or not we are currently editing the Dashboard
 * @param {int} id - the id of the dashboard
 * @param {bool} isLimitedToCreator - if the dashboard is exclusive to its creator
 * @param {Array} layout - this is the current layout of the RGL
 * @param {Array} limitedToTeams - teams associated with the dashboard
 * @param {string} name - the name of the dashboard
 * @param {bool} reset - whether or not we should reset the state of the dashboard
 * @param {Object} widgets - an object containing all widgets for the Dashboard
 */
export const initializeDashboard = ({
    cached_widget_data,
    creator,
    editing,
    external_password,
    external_regions,
    external_slug,
    externalDownloadsDisabled,
    id,
    is_external,
    isLimitedToCreator,
    layout,
    limitedToTeams,
    name,
    reset,
    widgets,
}) => ({
    cached_widget_data,
    creator,
    editing,
    external_password,
    external_regions,
    external_slug,
    externalDownloadsDisabled,
    id,
    is_external,
    is_limited_to_creator: isLimitedToCreator,
    layout,
    limitedToTeams,
    name,
    reset,
    type: dashboardsActionTypes.INITIALIZE_DASHBOARD,
    widgets,
})

/**
 * Updates the given key value pairs in values for dashboard id in the store
 *
 * @param {int} id - the id of the dashboard
 * @param {Object} values - the values to update in the store
 */
export const setDashboardsValues = ({ id, values } = {}) => ({
    type: dashboardsActionTypes.SET_DASHBOARD_VALUES,
    id,
    values,
})

/**
 * Removes key/value from layout/newLayout in the Dashboard store
 *
 * @param {int} id - the id of the dashboard
 * @param {Object} index - the widget key to remove from the layout array
 */
export const removeFromDashboardLayout = ({ id, index } = {}) => ({
    type: dashboardsActionTypes.REMOVE_FROM_DASHBOARD_LAYOUT,
    id,
    index,
})

/**
 * Ensures that newLayout and layout are always in parity with each other
 *
 * @param {int} id - the id of the dashboard
 */
export const reconcileDashboardLayout = ({ id } = {}) => ({
    type: dashboardsActionTypes.RECONCILE_DASHBOARD_LAYOUT,
    id,
})

/**
 * This action creator fetches a Dashboard
 *
 * @param {int} id - the id of the Dashboard to get
 * @returns {Promise} Promise with the resulting response
 */
export const loadDashboard = (id) => resourceGetDetail(Dashboard, actionDicts.loadDashboardActionDict, id, { id })

/**
 * This action creates or updates a Dashboard
 *
 * @param {int} id - the id of the Dashboard to get
 * @param {Object} values - the values to update in the db
 * @returns {Promise} Promise with the resulting response
 */
export const createOrUpdateDashboard =
    ({ id, values, customRegions }) =>
    (dispatch, getState) => {
        // redux-thunk provides access to the state, which means that we can now select our
        // previously unconnected newLayout prop from the dashboards store and update its value in our Dashboard
        // https://github.com/reduxjs/redux-thunk#motivation
        const layout = selectDashboardsValue("newLayout")(getState(), {
            params: {
                dashboardId: id,
            },
        })

        // if values.external_regions is array, it means the values is from duplicating dashboard, no action needed
        if (values.external_regions && !Array.isArray(values.external_regions)) {
            const form_region = values.external_regions.get
                ? values.external_regions.get("value")
                : values.external_regions.value
            const hasCustomRegion =
                typeof form_region === "string" && form_region.startsWith("/api/customregionselection")

            if (hasCustomRegion) {
                const selectedCustomRegion = customRegions.find(
                    (customRegion) => customRegion.resource_uri === form_region,
                )
                values.external_custom_region_selection = form_region
                values.external_regions = selectedCustomRegion.region_values.map((regionValue) => parseInt(regionValue))
            } else {
                values.external_regions = [form_region]
            }
        }

        if (id) {
            return dispatch(
                resourcePatchDetail(
                    Dashboard,
                    actionDicts.patchDashboardActionDict,
                    id,
                    { ...values, positions: JSON.stringify(layout) },
                    { id },
                ),
            )
        } else {
            return dispatch(
                resourcePostList(
                    Dashboard,
                    actionDicts.postDashboardActionDict,
                    {
                        organization: DjangIO.app.userdata.models.Organization.resourceUriFromId(
                            Userdata.organization_id,
                        ),
                        ...values,
                    },
                    {},
                ),
            )
        }
    }

/**
 * Toggles the create dashboard modal
 *
 * @param {int} page - which page of the modal we are to go to (0 indicates no modal)
 */
export const toggleCreateDashboardModal = (page = 0) => ({
    page,
    type: dashboardsActionTypes.TOGGLE_CREATE_DASHBOARD_MODAL,
})

export const chooseDashboardFormTemplate = (template) => ({
    template,
    type: dashboardsActionTypes.SELECT_DASHBOARD_FORM_TEMPLATE,
})

/**
 * Clears the stored dashboard templates from the store
 */
export const clearDashboardTemplateList = () => ({
    type: dashboardsActionTypes.DASHBOARD_CLEAR_TEMPLATE_LIST,
})

export const validateDashboardCreation = (values) => {
    const errors = {}

    if (!values.get("name")) {
        errors.name = "Please give your dashboard a name."
    }

    return errors
}

/**
 * creates a dashboard after serializing the form values. It is separate from createOrUpdateDashboard as
 * it specifically cares about the form input, such as mapping of templates to values, which is the only
 * way to create a dashboard
 *
 * @param {Immutable.Map} formValues - map of form keys to value
 * @param {function} dispatch - redux forms dispatch that maps to redux dispatch
 * @returns {Promise}
 */
export const createDashboard = async (formValues, dispatch, props) => {
    if (!formValues.get("name")) {
        return swal({
            icon: "error",
            title: "Oops!",
            text: "Please give your dashboard a name.",
        })
    }

    const values = {
        creator: DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(Userdata.id),
        external_downloads_disabled: formValues.get("externalDownloadsDisabled"),
        external_password: formValues.get("passwordRequired") ? formValues.get("external_password") : null,
        external_regions: formValues.get("external_regions") || null,
        external_slug: formValues.get("external_slug") || randomstring.generate({ length: 20, charset: "alphabetic" }),
        is_external: formValues.get("is_external"),
        is_limited_to_creator: formValues.get("is_limited_to_creator"),
        limited_to_teams: formValues.get("limited_to_teams"),
        name: formValues.get("name"),
        positions: formValues.get("positions") || [],
        widgets: formValues.get("widgets") || {},
    }

    const response = await dispatch(createOrUpdateDashboard({ values, customRegions: props.customRegions }))
    await dispatch(toggleCreateDashboardModal(0))
    sendUserActionPost({ userActionType: DjangIO.app.useractions.types.UserActionType.created_dashboard })
    return dispatch(segueToUrl(`${DjangIO.app.models.QuorumDataType.dashboard.profile}${response.id}/`))
}

/**
 * Used to transform permission formValues into params for createOrUpdateDashboard
 *
 * @param {Immutable.Map} formValues - map of form keys to value
 * @param {function} dispatch - redux forms dispatch that maps to redux dispatch
 * @returns {Promise}
 */
export const editDashboard = async (formValues, dispatch, props) => {
    const id = formValues.get("id")
    const values = {
        external_downloads_disabled: formValues.get("externalDownloadsDisabled"),
        external_password: formValues.get("passwordRequired") ? formValues.get("external_password") : null,
        external_regions: formValues.get("external_regions") || null,
        is_external: formValues.get("is_external"),
        is_limited_to_creator: formValues.get("is_limited_to_creator"),
        limited_to_teams: formValues.get("limited_to_teams"),
    }

    await dispatch(createOrUpdateDashboard({ id, values, customRegions: props.customRegions }))

    return dispatch(
        setDashboardsValues({
            id,
            values: {
                externalDownloadsDisabled: formValues.get("externalDownloadsDisabled"),
                ...values,
            },
        }),
    )
}

/**
 * Load a page of DashboardTemplates from the server
 *
 * @name getDashboardTemplates
 * @function
 * @param {Number} offset - the start of the page to fetch
 * @param {Object} filters - object with keys as filters to apply and values as values for those filters
 * @returns {Promise}  returns result of resourceGetList
 */
export const getDashboardTemplates = ({ offset = 0, filters = {} }) =>
    resourceGetList(
        DjangIO.app.widgets.models.DashboardTemplate,
        actionDicts.loadDashboardTemplateListActionDict,
        {
            ...filters,
            offset,
            limit: constants.widgetTemplateLimit,
        },
        {
            limit: constants.widgetTemplateLimit,
        },
    )

/**
 * function used to duplicate a dashboard, showing swalls and rerouting user as needed
 *
 * @param {Number} dashId - the id of the dashboard we are copying
 * @returns {Promise}
 */
export const duplicateDashboardFromListView = (dashId) => (dispatch, getState) => async () => {
    const swalNameResponse = await swal({
        title: "Duplicate Dashboard?",
        text: "If so, type a name for the duplicate of your dashboard.",
        content: {
            element: "input",
            attributes: {
                placeholder: "Name your duplicated dashboard...",
            },
        },
        buttons: {
            cancel: "Cancel",
            confirm: {
                text: "Duplicate",
                closeModal: false,
            },
        },
    })
    if (swalNameResponse === null) {
        // user cancelled the duplication
        return
    }

    // Load dashboard again to fetch 'widgets' and 'positions' fields, only available on detail request
    dispatch(loadDashboard(dashId)).then((dashboard) => {
        const name = swalNameResponse || ""

        const values = {
            creator: DjangIO.app.userdata.models.QuorumUser.resourceUriFromId(Userdata.id),
            external_custom_region_selection_id: dashboard.external_custom_region_selection,
            external_downloads_disabled: dashboard.external_downloads_disabled,
            external_password: dashboard.external_password,
            external_regions: dashboard.external_regions,
            external_slug: randomstring.generate({ length: 20, charset: "alphabetic" }),
            is_external: dashboard.is_external,
            is_limited_to_creator: dashboard.is_limited_to_creator,
            limited_to_teams: dashboard.limited_to_teams,
            name,
            positions: dashboard.positions,
            widgets: dashboard.widgets,
        }

        dispatch(createOrUpdateDashboard({ values })).then((response) => {
            swal({
                title: "Duplicated!",
                text: "Your dashboard has been duplicated.",
                icon: "success",
            })
            dispatch(segueToUrl(`${DjangIO.app.models.QuorumDataType.dashboard.profile}${response.id}/`))
        })
    })
}

export const duplicateDashboardButtonActions = {
    duplicateDashboardFromListView,
}
